import { heroKit } from '@/content/site'

/**
 * The five panels that make up the assembling brandkit.
 *
 * These are illustrative content, not real generator output — the point is the
 * *shape* of what NuCraft produces (palette, type, icons, mark, moodboard),
 * shown assembling in sequence.
 */

/** Simple line glyphs for the icon-set panel. */
const GLYPHS: Record<string, string> = {
  upload: 'M12 16V5M12 5l-4 4M12 5l4 4M5 18h14',
  shield: 'M12 4l6 2.5v5c0 3.6-2.5 6.5-6 7.5-3.5-1-6-3.9-6-7.5v-5z',
  spark: 'M12 4v5M12 15v5M4 12h5M15 12h5M6.5 6.5l3 3M14.5 14.5l3 3M17.5 6.5l-3 3M9.5 14.5l-3 3',
  layers: 'M12 4l7 3.5-7 3.5-7-3.5zM5 12l7 3.5 7-3.5M5 16l7 3.5 7-3.5',
  pulse: 'M3 12h4l2-5 3 10 2.5-5H21',
  route: 'M5 7h7a3 3 0 010 6H9a3 3 0 000 6h10',
}

export function PalettePanel() {
  return (
    <div className="flex h-full flex-col">
      <p className="micro mb-3">{heroKit.palette.label}</p>
      <div className="flex flex-1 items-end gap-2">
        {heroKit.palette.swatches.map((s) => (
          <div key={s.hex} className="flex flex-1 flex-col items-center gap-1.5">
            <span
              className="h-10 w-full rounded-lg"
              style={{ background: s.hex, boxShadow: 'inset 0 1px 0 rgb(255 255 255 / 0.18)' }}
              aria-hidden="true"
            />
            <span className="font-mono text-[0.55rem] tracking-tight text-ink-mute">
              {s.hex.slice(1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function TypePanel() {
  return (
    <div className="flex h-full flex-col">
      <p className="micro mb-3">{heroKit.type.label}</p>
      <div className="flex flex-1 items-center gap-4">
        <span className="font-sans text-[3.2rem] font-semibold leading-none tracking-tight text-ink">
          {heroKit.type.specimen}
        </span>
        <div className="flex flex-col gap-1">
          <span className="font-sans text-sm text-ink-dim">{heroKit.type.family}</span>
          {heroKit.type.scale.map((line) => (
            <span key={line} className="font-mono text-[0.55rem] tracking-wide text-ink-mute">
              {line}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

export function IconsPanel() {
  return (
    <div className="flex h-full flex-col">
      <p className="micro mb-3">{heroKit.icons.label}</p>
      <div className="grid flex-1 grid-cols-3 content-center gap-3">
        {heroKit.icons.glyphs.map((name) => (
          <svg
            key={name}
            viewBox="0 0 24 24"
            fill="none"
            className="mx-auto h-7 w-7 text-ink/80"
            aria-hidden="true"
          >
            <path
              d={GLYPHS[name]}
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ))}
      </div>
    </div>
  )
}

export function LogoPanel() {
  return (
    <div className="flex h-full flex-col">
      <p className="micro mb-3">{heroKit.logo.label}</p>
      <div className="flex flex-1 items-center justify-around gap-2">
        {heroKit.logo.variants.map((variant, i) => (
          <div key={variant} className="flex flex-col items-center gap-2">
            <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
              <path
                d="M16 5l9 5v12l-9 5-9-5V10z"
                fill={i === 0 ? 'rgb(var(--n-iris-rgb))' : 'none'}
                stroke={i === 2 ? 'rgb(var(--n-mute-rgb))' : 'rgb(var(--n-iris-rgb))'}
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
            <span className="font-mono text-[0.52rem] uppercase tracking-wider text-ink-mute">
              {variant}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function MoodboardPanel() {
  const tiles = [
    'linear-gradient(135deg, rgb(91 75 214 / 0.85), rgb(34 211 238 / 0.5))',
    'linear-gradient(135deg, rgb(244 114 182 / 0.75), rgb(167 139 250 / 0.5))',
    'linear-gradient(135deg, rgb(34 211 238 / 0.7), rgb(15 16 32 / 0.9))',
    'linear-gradient(135deg, rgb(251 191 36 / 0.6), rgb(244 114 182 / 0.5))',
  ]
  return (
    <div className="flex h-full flex-col">
      <p className="micro mb-3">{heroKit.moodboard.label}</p>
      <div className="grid flex-1 grid-cols-2 gap-2">
        {tiles.map((bg, i) => (
          <span
            key={i}
            className="rounded-lg"
            style={{ background: bg, minHeight: '2rem' }}
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  )
}
